import { Button, ConfigProvider, Modal, Segmented } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useSessionState from "../../../../hooks/useSessionState";
import { useToast } from "../../../../contexts/ToastContext";
import { useUser } from "../../../../contexts/UserContext/UserContext";
import useOpenModalParam from "../../../../hooks/useOpenModalParam";
import InsuranceConnectPrompt from "../InsuranceConnectPrompt";
import {
    endInsuranceSale,
    getEnabledSaleProviders,
    listInsuranceSales,
    startInsuranceSaleCoverage,
} from "../../lib/insuranceSaleApi";
import ExtendInsuranceModal from "./ExtendInsuranceModal";
import InsuranceSalesTable from "./InsuranceSalesTable";
import SellInsuranceModal from "./SellInsuranceModal";
import PaySaleStep from "./steps/PaySaleStep";

// Ended sales are checked out by the backend, so "past" is its own list.
const SALE_TABS = { ACTIVE: "active", PAST: "past" };

const SellInsurance = ({ refreshKey = 0 }) => {
    const { t } = useTranslation("sellInsurance");
    const toast = useToast();
    const location = useLocation();
    const { myCompanyData } = useUser();

    // Stable reference: the wizard resets itself whenever this array changes identity.
    const availableProviders = useMemo(() => getEnabledSaleProviders(myCompanyData), [myCompanyData]);

    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useSessionState("insurance-sales:page", 1);
    const [pageSize, setPageSize] = useSessionState("insurance-sales:pageSize", 20);
    const [totalItems, setTotalItems] = useState(0);
    const [searchParams, setSearchParams] = useState({});
    const [saleTab, setSaleTab] = useState(SALE_TABS.ACTIVE);
    const [modalOpen, setModalOpen] = useState(false);
    // Set when buying "for this customer" (e.g. from the customer page), so the wizard starts with them chosen.
    const [prefillCustomerId, setPrefillCustomerId] = useState(null);
    const [paySale, setPaySale] = useState(null);
    const [extendSale, setExtendSale] = useState(null);
    const [, setActionLoading] = useState(false);

    const hasProvider = availableProviders.length > 0;

    const openBuyModal = (customerId = null) => {
        setPrefillCustomerId(customerId);
        setModalOpen(true);
    };

    // ?openModal=newPolicySale[&customerId=...] — dashboard quick action and customer page deep link.
    useOpenModalParam(
        "newPolicySale",
        () => openBuyModal(new URLSearchParams(location.search).get("customerId")),
        hasProvider,
    );

    const fetchSales = useCallback(async () => {
        if (!hasProvider) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const { result, total } = await listInsuranceSales({
                page: currentPage,
                limit: pageSize,
                status: saleTab,
                searchTerm: searchParams.searchTerm,
                insuranceType: searchParams.insuranceType,
                isPaid: searchParams.isPaid,
            });
            setSales(result);
            setTotalItems(total);
        } catch (error) {
            toast.error({
                message: t("fetchError"),
                description: error?.response?.data?.message || error?.message,
            });
            setSales([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    }, [hasProvider, currentPage, pageSize, searchParams, saleTab, toast, t]);

    const handleTabChange = (tab) => {
        setSaleTab(tab);
        setCurrentPage(1);
    };

    useEffect(() => {
        fetchSales();
    }, [fetchSales, refreshKey]);

    const handleRetry = async (record) => {
        setActionLoading(true);
        try {
            const response = await startInsuranceSaleCoverage(record.id);
            if (response?.success === false) {
                throw new Error(response?.message || t("table.retryError"));
            }
            toast.success({ message: t("table.retrySuccess") });
            fetchSales();
        } catch (error) {
            toast.error({
                message: t("table.retryError"),
                description: error?.response?.data?.message || error?.message,
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleEnd = (record) => {
        Modal.confirm({
            title: t("table.endConfirmTitle"),
            content: t("table.endConfirmDescription", { id: record.id }),
            okText: t("table.end"),
            okButtonProps: { danger: true },
            onOk: async () => {
                setActionLoading(true);
                try {
                    const response = await endInsuranceSale(record.id);
                    if (response?.success === false) {
                        throw new Error(response?.message || t("table.endError"));
                    }
                    toast.success({
                        message: t("table.endSuccess"),
                        description: t("table.endSuccessMoved", { id: record.id }),
                    });
                    fetchSales();
                } catch (error) {
                    toast.error({
                        message: t("table.endError"),
                        description: error?.response?.data?.message || error?.message,
                    });
                    throw error;
                } finally {
                    setActionLoading(false);
                }
            },
        });
    };

    const pageHeading = (
        <div data-tour="buy-insurance-page">
            <h1 className="fontbold text-lg text-gray-900 sm:text-[1.2rem]">{t("pageTitle")}</h1>
            <p className="mt-1 text-sm text-gray-500">{t("pageSubtitle")}</p>
        </div>
    );

    if (!hasProvider) {
        return (
            <div className="flex min-w-0 flex-col gap-4">
                <div className="my-4 sm:my-5">{pageHeading}</div>
                <InsuranceConnectPrompt
                    title={t("connectPromptTitle")}
                    description={t("connectPromptDescription")}
                    settingsLabel={t("goToSettings")}
                />
            </div>
        );
    }

    return (
        <div className="min-w-0">
            <div className="my-4 flex flex-col gap-3 sm:my-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                {pageHeading}
                <Button
                    data-tour="insurance-sell-button"
                    type="primary"
                    size="large"
                    icon={<PlusOutlined />}
                    shape="round"
                    onClick={() => openBuyModal()}
                    className="w-full shrink-0 sm:w-auto">
                    {t("buyForCustomer")}
                </Button>
            </div>

            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-base font-semibold text-gray-900">
                    {t("title")} <span className="text-[#5d5dff]">({totalItems})</span>
                </div>
                <ConfigProvider
                    theme={{
                        components: {
                            Segmented: {
                                itemSelectedBg: "#5d5dff",
                                itemSelectedColor: "#ffffff",
                                trackPadding: "4px",
                            },
                        },
                    }}>
                    <Segmented
                        value={saleTab}
                        onChange={handleTabChange}
                        options={[
                            { label: t("tabActive"), value: SALE_TABS.ACTIVE },
                            { label: t("tabPast"), value: SALE_TABS.PAST },
                        ]}
                        shape="round"
                        className="w-full bg-white sm:w-auto [&_.ant-segmented-item]:flex-1 sm:[&_.ant-segmented-item]:flex-none [&_.ant-segmented-item-label]:px-5"
                    />
                </ConfigProvider>
            </div>

            <InsuranceSalesTable
                sales={sales}
                loading={loading}
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={totalItems}
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
                searchParams={searchParams}
                onSearchParamsChange={setSearchParams}
                onPay={setPaySale}
                onRetry={handleRetry}
                onExtend={setExtendSale}
                onEnd={handleEnd}
                onRefresh={fetchSales}
                isPast={saleTab === SALE_TABS.PAST}
            />

            <SellInsuranceModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setPrefillCustomerId(null);
                }}
                onSuccess={fetchSales}
                availableProviders={availableProviders}
                initialCustomerId={prefillCustomerId}
            />

            <Modal
                open={Boolean(paySale)}
                title={t("pay.title", { id: paySale?.id })}
                onCancel={() => setPaySale(null)}
                footer={null}
                destroyOnClose
                centered
                width={{
                    xs: "calc(100vw - 2rem)",
                    sm: 520,
                }}
                styles={{
                    body: { maxHeight: "min(80vh, 720px)", overflowY: "auto" },
                }}>
                {paySale && (
                    <PaySaleStep
                        reservationId={paySale.id}
                        amount={Number(paySale.invoice?.due ?? paySale.invoice?.total ?? 0)}
                        customerEmail={paySale.email}
                        reservationProcess="insuranceSale"
                        onSuccess={() => {
                            setPaySale(null);
                            fetchSales();
                        }}
                    />
                )}
            </Modal>

            <ExtendInsuranceModal
                open={Boolean(extendSale)}
                sale={extendSale}
                onClose={() => setExtendSale(null)}
                onSuccess={() => {
                    setExtendSale(null);
                    fetchSales();
                }}
            />
        </div>
    );
};

SellInsurance.propTypes = {
    refreshKey: PropTypes.number,
};

SellInsurance.defaultProps = {
    refreshKey: 0,
};

export default SellInsurance;
